import { Mutation } from "@apollo/client/react/components";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import React from "react";
import Switch from "react-switch";
import UpdateGovernmentSettings from "~/lib/mutations/UpdateGovernmentSettings";
import {
  IUpdateGovernmentSettingsMutation,
  IUpdateGovernmentSettingsMutationVariables,
} from "~/lib/mutations/__generated__/UpdateGovernmentSettings.generated";
import { IGovernmentQuery } from "~/lib/queries/__generated__/GovernmentQuery.generated";
import Dotdotdot from "~/components/text/Dotdotdot";
import { FormattedMessage } from "react-intl";

interface IProjectSharingWithGroupsProps {
  government: Pick<NonNullable<IGovernmentQuery["government"]>, "_id" | "settings" | "groups">;
}

export default function ProjectSharingWithGroups({ government }: IProjectSharingWithGroupsProps) {
  return (
    <div
      data-intercom-target="Project Sharing with Groups"
      css={{
        backgroundColor: palette.white,
        border: `solid 1px ${palette.lightestGray}`,
        borderRadius: 4,
      }}
    >
      <div
        css={{
          padding: 16,
          textTransform: "uppercase",
          fontSize: 12,
          borderBottom: `1px solid ${palette.lightestGray}`,
          color: palette.sealBlue,
          letterSpacing: 0.5,
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
        }}
      >
        <FormattedMessage defaultMessage="Group Project Sharing" id="KCUJSY" />
      </div>

      <div
        css={{
          display: "grid",
          gridRowGap: 24,
          padding: 16,
        }}
      >
        <p
          css={{
            margin: 0,
            color: palette.mediumGray,
            fontSize: 14,
            lineHeight: 1.33,
            letterSpacing: 1,
          }}
        >
          <FormattedMessage defaultMessage="By enabling sharing with a Group, all Public and Community projects from your Government will be displayed in the Group. You can disable this at any time." id="iPKgNY" />
        </p>

        <Mutation<IUpdateGovernmentSettingsMutation, IUpdateGovernmentSettingsMutationVariables>
          mutation={UpdateGovernmentSettings}
        >
          {(updateGovernmentSettings) => (
            <div>
              {government.groups.map((group) => {
                return (
                  <div
                    key={group._id}
                    css={{
                      display: "grid",
                      gridTemplateColumns: "48px 1fr max-content",
                      alignItems: "center",
                      gridColumnGap: 20,
                    }}
                  >
                    <ProfilePicture size={48} image={group.logo} name={group.name} />

                    <Dotdotdot clamp={1}>
                      <p
                        css={{
                          margin: 0,
                          color: palette.text,
                          fontSize: 14,
                          lineHeight: 1.33,
                        }}
                      >
                        {group.name}
                      </p>
                    </Dotdotdot>

                    <Switch
                      uncheckedIcon={
                        <div
                          css={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            paddingRight: 20,
                            color: palette.lightRed,
                            fontSize: 12,
                          }}
                        >
                          off
                        </div>
                      }
                      checkedIcon={
                        <div
                          css={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            paddingLeft: 20,
                            color: "#fff",
                            fontSize: 12,
                          }}
                        >
                          on
                        </div>
                      }
                      offColor={palette.red}
                      handleDiameter={22}
                      height={24}
                      width={80}
                      onColor={palette.greenSuccess}
                      onChange={(isChecked) => {
                        let groups = government.settings.projectSharingWithGroups;

                        if (isChecked) {
                          groups = groups.concat(group);
                        } else {
                          groups = groups.filter((maybe) => maybe._id !== group._id);
                        }

                        updateGovernmentSettings({
                          variables: {
                            governmentId: government._id,
                            settings: {
                              projectSharingWithGroups: groups.map((group) => {
                                return group._id;
                              }),
                            },
                          },
                        });
                      }}
                      checked={government.settings.projectSharingWithGroups.some((maybe) => {
                        return maybe._id === group._id;
                      })}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </Mutation>
      </div>
    </div>
  );
}
